// src/pages/PublicCareers.js
import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Container,
    Row,
    Col,
    Input,
    Button,
    Badge,
    InputGroup,
    InputGroupText,
    Card,
    CardBody,
} from "reactstrap";
import {
    FiSearch,
    FiMapPin,
    FiClock,
    FiBriefcase,
    FiFilter,
    FiX,
    FiChevronRight,
} from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import "./careers.css";
import { getAllJobs } from "../../services/api";

function Careers() {
    const [search, setSearch] = useState("");
    const [locationFilter, setLocationFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [experienceFilter, setExperienceFilter] = useState("all"); 
    const [showFilters, setShowFilters] = useState(false);
    const [jobs, setJobs] = useState([]);
    // console.log("jobs--------", jobs);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Transform API data to match your component's expected structure
    const activeJobs = useMemo(() => {
        return jobs
            .filter((job) => job.status === "Published") // Only show published jobs
            .map((job) => ({
                id: job.id,
                title: job.title,
                department: job.department,
                location: job.location?.trim(),
                type: job.employmentType,
                experience: job.experienceRequired?.trim(),
                salary: job.salaryRange,
                description: job.description,
                skills: job.requiredSkills 
                    ? job.requiredSkills.split(",").map(s => s.trim()).filter(Boolean)
                    : [],
                status: job.status,
                postedDate: new Date(job.createdAt).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                }) + "", // Simple fallback — you can improve this later
            }));
    }, [jobs]);

    const locations = [...new Set(activeJobs.map((j) => j.location))];
    const types = [...new Set(activeJobs.map((j) => j.type))];
    const experiences = [...new Set(activeJobs.map((j) => j.experience))];

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getAllJobs();
                setJobs(data || []); // array of jobs from your API response
            } catch (err) {
                setError(err.message || "Failed to load jobs. Please try again.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchJobs();
    }, []);

    const filtered = useMemo(() => {
        return activeJobs.filter((job) => {
            const matchesSearch =
                !search ||
                job.title.toLowerCase().includes(search.toLowerCase()) ||
                job.department.toLowerCase().includes(search.toLowerCase()) ||
                job.description.toLowerCase().includes(search.toLowerCase()) ||
                job.skills.some((s) => s.toLowerCase().includes(search.toLowerCase()));

            const matchesLocation = locationFilter === "all" || job.location === locationFilter;
            const matchesType = typeFilter === "all" || job.type === typeFilter;
            const matchesExperience = experienceFilter === "all" || job.experience === experienceFilter;

            return matchesSearch && matchesLocation && matchesType && matchesExperience;
        });
    }, [search, locationFilter, typeFilter, experienceFilter, activeJobs]);

    const activeFilterCount = [locationFilter, typeFilter, experienceFilter].filter(
        (f) => f !== "all"
    ).length;

    const clearFilters = () => {
        setLocationFilter("all");
        setTypeFilter("all");
        setExperienceFilter("all");
        setSearch("");
    };

    // Animation variants
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.08 },
        },
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 },
    };

    if (loading) {
        return (
            <div className="public-careers-wrapper min-vh-100 bg-light d-flex align-items-center justify-content-center">
                <h4>Loading opportunities...</h4>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-careers-wrapper min-vh-100 bg-light d-flex align-items-center justify-content-center">
                <div className="text-center">
                    <h5 className="text-danger">{error}</h5>
                    <Button color="primary" onClick={() => window.location.reload()}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="public-careers-wrapper min-vh-100 bg-light">
            <div className="">
                {/* Hero Section - unchanged */}
                <section className="public-careers-hero bg-gradient-primary text-white position-relative overflow-hidden py-lg-7 py-md-7">
                    <div className="hero-blobs">
                        <div className="blob-1" />
                        <div className="blob-2" />
                    </div>

                    <Container className="position-relative text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                        >
                            <span pill className="public-careers-hiring-badge mb-3 px-4 py-2 ">
                                We're Hiring
                            </span>
                        </motion.div>
                        <motion.h1
                            className="public-careers-hero-title fw-bold mb-3 mt-2"
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                        >
                            Find Your Dream Career
                        </motion.h1>

                        <motion.p
                            className="public-careers-hero-text lead mx-auto mb-5 opacity-85"
                            initial={{ opacity: 0, y: 35 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.4, ease: "easeOut" }}
                        >
                            Join our team of innovators and build the future of technology.
                            Explore open positions and take the next step in your career.
                        </motion.p>

                        <motion.div
                            className="public-careers-search-container mx-auto"
                            initial={{ opacity: 0, scale: 0.96, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                        >
                            <InputGroup className="shadow rounded-pill bg-white overflow-hidden">
                                <InputGroupText className="bg-transparent border-0 ps-4">
                                    <FiSearch size={18} className="text-muted" />
                                </InputGroupText>
                                <Input
                                    placeholder="Search jobs, skills, keywords..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="border-0 py-3 fs-6"
                                />
                            </InputGroup>
                        </motion.div>
                    </Container>
                </section>

                {/* Filters & Results - unchanged logic */}
                <Container className="py-5">
                    <Row className="align-items-center mb-4">
                        <Col xs={12} md={6} className="mb-3 mb-md-0">
                            <p className="mb-0 text-muted fs-6">
                                <strong className="text-dark">{filtered.length}</strong> open positions
                            </p>
                            {activeFilterCount > 0 && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 mt-1 d-flex align-items-center gap-1 text-primary"
                                    onClick={clearFilters}
                                >
                                    <FiX size={14} /> Clear filters
                                </Button>
                            )}
                        </Col>

                        <Col xs={12} md={6} className="text-md-end ">
                            <Button
                                outline
                                color="secondary"
                                size="sm"
                                className="d-flex align-items-center gap-2 mx-auto float-end filter-btn"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <FiFilter size={16} />
                                Filters
                                {activeFilterCount > 0 && (
                                    <Badge pill className="ms-2 px-2 py-1">
                                        {activeFilterCount}
                                    </Badge>
                                )}
                            </Button>
                        </Col>
                    </Row>

                    {/* Filter Panel - unchanged */}
                    {showFilters && (
                        <Card className="border-0 shadow-sm mb-5 rounded-4">
                            <CardBody className="p-4">
                                <Row>
                                    <Col md={4} className="mb-4 mb-md-0">
                                        <label className="small fw-semibold text-muted mb-2 d-block text-uppercase">
                                            Location
                                        </label>
                                        <Input
                                            type="select"
                                            value={locationFilter}
                                            onChange={(e) => setLocationFilter(e.target.value)}
                                            className="rounded-3"
                                        >
                                            <option value="all">All Locations</option>
                                            {locations.map((loc) => (
                                                <option key={loc} value={loc}>
                                                    {loc}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md={4} className="mb-4 mb-md-0">
                                        <label className="small fw-semibold text-muted mb-2 d-block text-uppercase">
                                            Job Type
                                        </label>
                                        <Input
                                            type="select"
                                            value={typeFilter}
                                            onChange={(e) => setTypeFilter(e.target.value)}
                                            className="rounded-3"
                                        >
                                            <option value="all">All Types</option>
                                            {types.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md={4}>
                                        <label className="small fw-semibold text-muted mb-2 d-block text-uppercase">
                                            Experience
                                        </label>
                                        <Input
                                            type="select"
                                            value={experienceFilter}
                                            onChange={(e) => setExperienceFilter(e.target.value)}
                                            className="rounded-3"
                                        >
                                            <option value="all">All Levels</option>
                                            {experiences.map((exp) => (
                                                <option key={exp} value={exp}>
                                                    {exp}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    )}

                    {/* Job Cards */}
                    {filtered.length === 0 ? (
                        <div className="text-center py-5 my-5">
                            <div className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: 80, height: 80 }}>
                                <FiBriefcase size={36} className="text-muted" />
                            </div>
                            <h5 className="mb-3 fw-semibold">No jobs found</h5>
                            <p className="text-muted mb-4">Try adjusting your filters or search term.</p>
                            <Button color="outline-secondary" size="sm" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </div>
                    ) : (
                        <motion.div
                            variants={container}
                            initial="hidden"
                            animate="show"
                            className="public-careers-job-list"
                        >
                            {filtered.map((job, index) => (
                                <motion.div key={job.id} variants={item}>
                                    <Link to={`/careers/${job.id}`} className="text-decoration-none d-block">
                                        <Card className="public-careers-job-item border-0 shadow-sm mb-4 rounded-4 hover-lift">
                                            <CardBody className="p-4">
                                                <Row className="align-items-center g-4">
                                                    <Col lg={8}>
                                                        <div className="d-flex gap-3">
                                                            <div className="d-none d-sm-flex align-items-center justify-content-center rounded bg-light flex-shrink-0" style={{ width: 52, height: 52 }}>
                                                                <LuBuilding2 size={22} className="lu-building" />
                                                            </div>

                                                            <div>
                                                                <h5 className="mb-2 fw-semibold text-dark career-job-title">
                                                                    {job.title}
                                                                </h5>

                                                                <div className="d-flex flex-wrap gap-3 mb-3 text-muted small">
                                                                    <span className="d-flex align-items-center gap-2">
                                                                        <FiMapPin size={14} /> {job.location}
                                                                    </span>
                                                                    <span className="d-flex align-items-center gap-2">
                                                                        <FiClock size={14} /> {job.experience}
                                                                    </span>
                                                                    <span className="d-flex align-items-center gap-2">
                                                                        <FiBriefcase size={14} /> {job.type}
                                                                    </span>
                                                                </div>

                                                                <p className="text-muted small mb-3 line-clamp-2">
                                                                    {job.description}
                                                                </p>

                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {job.skills.slice(0, 4).map((skill) => (
                                                                        <span
                                                                            key={skill}
                                                                            className="px-3 py-1 rounded-pill skill-badge"
                                                                        >
                                                                            {skill}
                                                                        </span>
                                                                    ))}
                                                                    {job.skills.length > 4 && (
                                                                        <span className="px-3 py-1 rounded-pill fs-6 fw-normal skill-badge">
                                                                            +{job.skills.length - 4}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>

                                                    <Col lg={4} className="text-lg-end mt-3 mt-lg-0">
                                                        {job.salary && (
                                                            <div className="fw-semibold mb-2 fs-6">
                                                                {job.salary}
                                                            </div>
                                                        )}
                                                        <div className="text-muted small mb-3">
                                                            Posted On {job.postedDate}
                                                        </div>
                                                        <div className="d-inline-flex align-items-center gap-2 small fw-medium careers-view-details">
                                                            View Details <FiChevronRight size={14} />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </Link>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </Container>
            </div>
        </div>
    );
}

export default Careers;